// src/pages/access/PendingTab.tsx — Phase 9 c10.
//
// Lists hub_users where sign_on_status === 'pending' (i.e. users who were
// invited but haven't redeemed their PIN yet). Each row gets Resend +
// Cancel actions. Both call /api/access/invitations/:id/{resend,DELETE}.
//
// Error handling:
//   - INVITE_ALREADY_ACCEPTED (409) — the user just logged in between the
//     list load and the action. Refresh the list rather than show an error
//     toast; the row should disappear on its own.
//
// No mutation of pending rows in-place — we trigger a refresh of the parent
// list on action.

import { useState } from "react";
import { apiFetch, ApiError } from "../../api";
import { C, F } from "../../theme";
import { Avatar, Button, Card, Stamp } from "../../components/ui";
import type { AccessUser } from "./types";

interface PendingTabProps {
  pending: AccessUser[];
  onRefresh: () => void;
  onOpen: (userId: number) => void;
}

interface PendingResendResponse {
  ok: true;
  id: number;
  inviteExpiresAt: string;
  emailed: boolean;
}

export function PendingTab({ pending, onRefresh, onOpen }: PendingTabProps) {
  const [busyId, setBusyId] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  async function resend(u: AccessUser) {
    setBusyId(u.id);
    setErrorMsg(null);
    setFlash(null);
    try {
      const res = await apiFetch<PendingResendResponse>(
        `/api/access/invitations/${u.id}/resend`,
        { method: "POST" },
      );
      setFlash(
        res.emailed
          ? `Invitation re-sent to ${u.email}.`
          : `Re-sent: PIN rotated, but email failed. Reveal the PIN from the user record.`,
      );
      onRefresh();
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const b = e.body as { error?: string; message?: string };
        if (b.error === "INVITE_ALREADY_ACCEPTED") {
          setFlash(`${u.email} signed in just now — list refreshing.`);
          onRefresh();
        } else {
          setErrorMsg(b.message ?? e.message);
        }
      } else {
        setErrorMsg(e instanceof ApiError ? e.message : "Resend failed.");
      }
    } finally {
      setBusyId(null);
    }
  }

  async function cancel(u: AccessUser) {
    if (!window.confirm(`Cancel invitation for ${u.email}? The user row will be deleted.`)) {
      return;
    }
    setBusyId(u.id);
    setErrorMsg(null);
    setFlash(null);
    try {
      await apiFetch(`/api/access/invitations/${u.id}`, { method: "DELETE" });
      setFlash(`Cancelled invitation for ${u.email}.`);
      onRefresh();
    } catch (e) {
      if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
        const b = e.body as { error?: string; message?: string };
        if (b.error === "INVITE_ALREADY_ACCEPTED") {
          setFlash(`${u.email} signed in just now — list refreshing.`);
          onRefresh();
        } else {
          setErrorMsg(b.message ?? e.message);
        }
      } else {
        setErrorMsg(e instanceof ApiError ? e.message : "Cancel failed.");
      }
    } finally {
      setBusyId(null);
    }
  }

  if (pending.length === 0) {
    return (
      <Card>
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 15,
            color: C.inkMute,
            textAlign: "center",
            padding: "16px 0",
          }}
        >
          No outstanding invitations.
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {flash && (
        <div
          role="status"
          style={{
            background: C.sageBg,
            border: `1px solid ${C.sageLt}`,
            borderRadius: 12,
            padding: "10px 14px",
            fontFamily: F.body,
            fontSize: 14,
            fontWeight: 700,
            color: C.sageDk,
          }}
        >
          {flash}
        </div>
      )}
      {errorMsg && (
        <div
          style={{
            background: C.signalBg,
            border: `1px solid rgba(184,58,43,0.22)`,
            borderRadius: 12,
            padding: "10px 14px",
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 700,
            color: C.signalDk,
          }}
        >
          {errorMsg}
        </div>
      )}

      <Card padded={false}>
        {pending.map((u, i) => (
          <PendingRow
            key={u.id}
            user={u}
            isFirst={i === 0}
            busy={busyId === u.id}
            onResend={() => resend(u)}
            onCancel={() => cancel(u)}
            onOpen={() => onOpen(u.id)}
          />
        ))}
      </Card>
    </div>
  );
}

function PendingRow({
  user,
  isFirst,
  busy,
  onResend,
  onCancel,
  onOpen,
}: {
  user: AccessUser;
  isFirst: boolean;
  busy: boolean;
  onResend: () => void;
  onCancel: () => void;
  onOpen: () => void;
}) {
  return (
    <div
      style={{
        padding: "14px 16px",
        borderTop: isFirst ? "none" : `1px solid ${C.hairlineLt}`,
      }}
    >
      <div
        style={{ display: "grid", gridTemplateColumns: "44px 1fr", gap: 12, cursor: "pointer" }}
        onClick={onOpen}
      >
        <Avatar first={user.first_name} last={user.last_name} size={44} tone="brass" />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span
              style={{
                fontFamily: F.serif,
                fontSize: 16,
                fontWeight: 700,
                color: C.ink,
                letterSpacing: -0.2,
              }}
            >
              {user.first_name || "(no name)"} {user.last_name}
            </span>
            <Stamp tone="brass">Pending sign-on</Stamp>
          </div>
          <div
            style={{
              fontFamily: F.body,
              fontSize: 12,
              color: C.inkSoft,
              fontWeight: 600,
              marginTop: 2,
            }}
          >
            {user.email}
          </div>
          {user.departments.length > 0 && (
            <div
              style={{
                fontFamily: F.body,
                fontSize: 11,
                color: C.inkMute,
                fontWeight: 500,
                marginTop: 4,
              }}
            >
              {user.departments.join(", ")}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <Button variant="secondary" onClick={onResend} disabled={busy}>
          {busy ? "Working…" : "Resend"}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel invitation
        </Button>
      </div>
    </div>
  );
}
